import { useEffect, useRef, type ReactNode } from "react";
import {
  AnimatePresence,
  motion,
  useIsPresent,
  useReducedMotion,
} from "motion/react";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useStoreWithEqualityFn } from "zustand/traditional";

import {
  campaignHook,
  campaignKey,
  collectCampaignEntries,
  matchesKey,
  type CampaignHook,
} from "@/campaigns";
import { CampaignCountdown } from "@/components/campaign-card/campaign-countdown";
import { CARD_BODY_TEXT } from "@/components/campaigns/campaign-shell/card-body-text";
import { PrizeText } from "@/components/prize-text";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useAppStore } from "@/stores";
import type { CampaignSchema } from "@/types";
import { campaignDisplayTitle } from "@/utils/campaign-helpers";

import type { OptInPromptPhase } from "./use-opt-in-prompt";

const sameHook = (a: CampaignHook | null, b: CampaignHook | null) =>
  a === b ||
  (a != null &&
    b != null &&
    a.label === b.label &&
    a.value === b.value &&
    a.isCatalogItem === b.isCatalogItem);

/** The type's own hook line ("Top prize · €2,000 Cash"), from its module — compared by value, so
 *  a progress push that leaves it unchanged does not re-render the prompt. */
const useCampaignHook = (campaign: CampaignSchema) => {
  const { t } = useTranslation();
  const key = campaignKey(campaign);
  return useStoreWithEqualityFn(
    useAppStore,
    (s) => {
      const owned = collectCampaignEntries(s, "active").find((o) =>
        matchesKey(o.entry.campaign, key)
      );
      return owned ? campaignHook(owned, t) : null;
    },
    sameHook
  );
};

/**
 * Wraps a campaign page's Overview body. While the prompt is up, the body stays rendered beneath
 * it — dimmed by the layer's scrim and `inert`, so neither a pointer nor Tab nor a screen reader
 * can reach content the player cannot see — and the layer sits on top. Not a dialog: there is no
 * focus trap and nothing to escape from; the cover, tabs, close and campaign sheet stay live, so
 * the player can still look around (Info / T&C), switch or leave.
 */
export type OptInPromptLayout = "stack" | "landscape" | "desktop";

export const OptInPromptFrame = ({
  campaign,
  active,
  phase,
  onJoin,
  onDismiss,
  layout,
  className,
  children,
}: {
  campaign: CampaignSchema;
  active: boolean;
  phase: OptInPromptPhase;
  onJoin: () => void;
  onDismiss: () => void;
  /** `stack`: phones portrait / on-site, full width under the tabs. `landscape`: a phone held
   *  sideways — one short horizontal card (the area under the header is ~260px tall). `desktop`:
   *  a readable column near the top of the content card. */
  layout: OptInPromptLayout;
  /** The ROOT's classes: it takes the body's place in its parent's layout (flex/grid sizing), so
   *  the caller passes what the body needs from it; the body itself renders unchanged inside a
   *  `display: contents` wrapper. */
  className?: string;
  children: ReactNode;
}) => (
  // `tabIndex={-1}`: the leaving prompt hands focus to this frame (see the layer), so a keyboard
  // or screen-reader player continues from the page the prompt covered, not from the top of the
  // casino page.
  <div
    tabIndex={-1}
    className={cn(
      "relative outline-none",
      active && layout !== "landscape" && "min-h-[26rem]",
      className
    )}
  >
    {/* `contents`: no box of its own, so the body lays out exactly as it would unwrapped. */}
    <div inert={active} className={cn("contents", active && "select-none")}>
      {children}
    </div>
    <AnimatePresence>
      {active && (
        <OptInPromptLayer
          key={campaignKey(campaign)}
          campaign={campaign}
          phase={phase}
          onJoin={onJoin}
          onDismiss={onDismiss}
          layout={layout}
        />
      )}
    </AnimatePresence>
  </div>
);

const OptInPromptLayer = ({
  campaign,
  phase,
  onJoin,
  onDismiss,
  layout,
}: {
  campaign: CampaignSchema;
  phase: OptInPromptPhase;
  onJoin: () => void;
  onDismiss: () => void;
  layout: OptInPromptLayout;
}) => {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const hook = useCampaignHook(campaign);
  const regionRef = useRef<HTMLDivElement>(null);
  const sideways = layout === "landscape";
  // Leaving: from the first frame of the exit it no longer catches taps nor speaks — if the
  // frameloop stalls (hidden tab, a starved host page) the fade may never finish, and an invisible
  // layer must not keep blocking the page it just handed back.
  const present = useIsPresent();

  // Announce it: focus moves to the labelled region when it appears (no trap — Tab carries on to
  // its buttons, then out to the rest of the panel). `preventScroll`: the page is pinned.
  useEffect(() => {
    regionRef.current?.focus({ preventScroll: true });
  }, []);
  // Leaving with the focus still inside (the Opt In or Not now the player just pressed): hand it
  // to the frame the prompt covered — a named successor, in the page the player is now on — before
  // the subtree goes `aria-hidden`. Never `blur()`: focus would fall to the document and a
  // keyboard player would start again from the top of the casino page.
  useEffect(() => {
    const region = regionRef.current;
    if (present || !region) return;
    const root = region.getRootNode() as Document | ShadowRoot;
    const focused = root.activeElement;
    if (!(focused instanceof HTMLElement) || !region.contains(focused)) return;
    const frame = region.parentElement;
    if (frame instanceof HTMLElement) frame.focus({ preventScroll: true });
    else focused.blur();
  }, [present]);

  const busy = phase === "joining";

  const pitch = (
    <div className={cn("flex min-w-0 flex-col", sideways ? "gap-5" : "gap-3")}>
      <div className="flex flex-col gap-1.5">
        <p className="font-heading text-[18px] leading-6 font-bold text-foreground lg:text-[20px]">
          {t("campaign.notJoinedTitle", "You haven't joined yet")}
        </p>
        {!sideways && (
          <p className={CARD_BODY_TEXT}>
            {t(
              "optInPrompt.body",
              "Opt in to take part. Every qualifying bet counts from then on."
            )}
          </p>
        )}
      </div>
      {hook && (
        <div className="flex min-w-0 flex-col gap-1">
          <span className="font-heading text-[10px] leading-4 font-semibold tracking-wider text-muted-foreground uppercase lg:text-[11px]">
            {hook.label}
          </span>
          {/* One line, like every prize: shrinks, then glides — never wraps, never truncates. */}
          <PrizeText
            size={sideways ? "medium" : "title"}
            icon
            currencyMark={!hook.isCatalogItem}
            fit="marquee"
            className="min-w-0"
          >
            {hook.value}
          </PrizeText>
        </div>
      )}
      {/* Portrait phones only: sideways the header chip already shows the time left. */}
      {layout === "stack" && (
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant="ghost"
            color="neutral"
            className="font-medium [&_svg]:size-3"
          >
            <CampaignCountdown campaign={campaign} variant="inline" compact />
          </Badge>
        </div>
      )}
    </div>
  );

  const actions =
    phase === "joined" ? (
      <p
        role="status"
        className="flex h-12 items-center justify-center gap-2 font-heading text-[16px] font-bold text-active"
      >
        <Check className="size-5" strokeWidth={3} />
        {t("optInPrompt.success", "You're in!")}
      </p>
    ) : (
      <div className={cn("flex flex-col", sideways ? "w-48 gap-2" : "gap-1")}>
        {phase === "failed" && (
          <p
            role="alert"
            className="text-center font-body text-[13px] text-urgent"
          >
            {t("optInPrompt.failed", "We couldn't opt you in.")}
          </p>
        )}
        <Button
          onClick={onJoin}
          disabled={busy}
          aria-busy={busy}
          className={cn(
            "w-full font-bold",
            sideways ? "h-10 text-[14px]" : "h-12 text-[16px]"
          )}
        >
          {busy ? (
            <>
              <Loader2 className="size-4 animate-spin motion-reduce:animate-none" />
              {t("optInPrompt.joining", "Opting you in…")}
            </>
          ) : phase === "failed" ? (
            t("prizeReveal.tryAgain", "Try again")
          ) : (
            <>
              {t("campaign.optIn", "Opt In")}
              <ArrowRight className="size-[18px]" strokeWidth={2.5} />
            </>
          )}
        </Button>
        {/* The decline as quiet text under the primary: an escape, not a second choice. */}
        <Button
          variant="quiet"
          onClick={onDismiss}
          disabled={busy}
          className="w-full text-[14px]"
        >
          {t("optInPrompt.notNow", "Not now")}
        </Button>
      </div>
    );

  return (
    <motion.div
      ref={regionRef}
      role="region"
      aria-label={t("optInPrompt.regionLabel", "Opt in to {{title}}", {
        title: campaignDisplayTitle(campaign, t),
      })}
      tabIndex={-1}
      aria-hidden={!present || undefined}
      className={cn(
        "absolute inset-0 z-30 flex flex-col items-center justify-start outline-none",
        !present && "pointer-events-none",
        sideways && "justify-center",
        layout === "desktop" && "px-4 pt-6"
      )}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: reduced ? 0 : 0.2 } }}
      transition={{ duration: reduced ? 0 : 0.2 }}
    >
      {/* Dims the page under the layer: the page colour, heavier toward the foot. */}
      <div aria-hidden className="opt-in-prompt-scrim absolute inset-0" />
      <motion.div
        className={cn(
          "relative w-full",
          layout === "stack" && "pt-1",
          sideways && "max-w-[600px]",
          layout === "desktop" && "max-w-[420px]"
        )}
        initial={reduced ? false : { y: 16, scale: 0.98 }}
        animate={{ y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 520, damping: 38 }}
      >
        <Card
          active
          className={cn(
            "opt-in-prompt-card",
            sideways
              ? // Sideways: more air than the card's phone inset — the short card read cramped.
                "flex items-center justify-between gap-8 px-7 py-8"
              : "flex flex-col gap-4"
          )}
        >
          {pitch}
          {actions}
        </Card>
      </motion.div>
    </motion.div>
  );
};
